/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Disclosure } from '@cimpress-ui/react';
import ItemRoutingDecisionViewer from '../ItemRoutingDecisionViewer/ItemRoutingDecisionViewer';
import { Environment, Item } from '../../types';
import { isItemSuspendedInRouting } from '../../helpers/item';
import { RetryAllButton } from './RetryAllButton';

type RoutingDecisionListBodyProps = {
  environment: Environment;
  accessToken: string;
  showBodyOnly?: boolean;
  showDecisionLinks?: boolean;
  items: Item[];
};

export const RoutingDecisionListBody = ({
  items,
  showBodyOnly = false,
  environment,
  accessToken,
  ...rest
}: RoutingDecisionListBodyProps) => {
  const suspendedItems = items.filter((item) => isItemSuspendedInRouting(item));
  const hasSuspendedItems = !!suspendedItems.length;

  if (showBodyOnly) {
    return (
      <div
        css={bodyOnlyStyles}
        className="body-styles-only"
        data-testid="routing-decision-list-body-only"
        data-cim-style-root
      >
        <ul css={listStyles}>
          {items.map((item) => (
            <li key={item.itemId}>
              <Disclosure
                title={`${item.orderedSkuCode} - ${item.merchantProductName}`}
                UNSAFE_className="item-accordion"
              >
                <ItemRoutingDecisionViewer
                  item={item}
                  isModal={false}
                  showModal={false}
                  environment={environment}
                  accessToken={accessToken}
                  {...rest}
                />
              </Disclosure>
            </li>
          ))}
        </ul>
        {hasSuspendedItems && <RetryAllButton environment={environment} accessToken={accessToken} items={items} />}
      </div>
    );
  }

  return (
    <div data-cim-style-root css={innerDiscloserStyles}>
      <Disclosure title="Items" UNSAFE_className="outer-accordion" data-testid="routing-decision-list-body-accordion">
        <RetryAllButton environment={environment} accessToken={accessToken} items={items} />

        <ul css={listStyles}>
          {items.map((item) => (
            <li key={item.itemId}>
              <Disclosure
                title={`${item.orderedSkuCode} - ${item.merchantProductName}`}
                UNSAFE_className="item-accordion"
              >
                <ItemRoutingDecisionViewer
                  item={item}
                  isModal={false}
                  showModal={false}
                  environment={environment}
                  accessToken={accessToken}
                  {...rest}
                />
              </Disclosure>
            </li>
          ))}
        </ul>
      </Disclosure>
    </div>
  );
};

const bodyOnlyStyles = css`
  height: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const innerDiscloserStyles = css`
  width: 100%;
`;

const listStyles = css`
  list-style-type: none;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin-left: 0;
  padding-left: 0;

  li:last-of-type .item-accordion {
    margin-bottom: 0;
  }
`;
